import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import type { Theme } from '@mui/material/styles'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { PromotionalCode } from '@leadgen/models/client'
import { Control, useController } from 'react-hook-form'
import type { ChangeEvent, FocusEvent } from 'react'
import CircularProgress from '../../../atoms/CircularProgress'
import promoCodeForm from '../../../../forms/promoCodeForm/promoCodeForm'

type PromoCodeFormData = {
  promotionalCode: string
}

const getTitleByCodeMode = ({ promotionalCode }: { promotionalCode: PromotionalCode }) => {
  switch (promotionalCode.mode) {
    case 'first-purchase': {
      return `(+${promotionalCode.value}% credits on first purchase)`
    }
    default: {
      return ''
    }
  }
}

const PromoBox: FC<{
  control: Control<PromoCodeFormData, any>
  appliedPromotionalCode: PromotionalCode | null
  promoCodeSubmitting: boolean
  promotionalCodeBootstrapping: boolean
  disabled?: boolean
  onApplyPromoCode: () => Promise<void>
  onClearAppliedPromoCode: () => void
}> = ({
  control,
  appliedPromotionalCode,
  promoCodeSubmitting,
  promotionalCodeBootstrapping,
  disabled = false,
  onApplyPromoCode,
  onClearAppliedPromoCode,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const {
    field: promotionalCodeField,
    fieldState: { error: promotionalCodeFieldError },
  } = useController({
    name: 'promotionalCode',
    control,
    rules: {
      validate: promoCodeForm.validatePromoCodeField,
    },
  })
  const [isEditing, setIsEditing] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const hasAppliedPromo = !!appliedPromotionalCode
  const appliedPromotionalCodeId = appliedPromotionalCode?.id
  const handleRemoveAppliedPromoCode = useCallback(() => {
    onClearAppliedPromoCode()
    setIsEditing(false)
  }, [onClearAppliedPromoCode])
  const handleApplyPromoCode = useCallback(async () => {
    await onApplyPromoCode()
  }, [onApplyPromoCode])
  const handlePromotionalCodeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      promotionalCodeField.onChange(event.target.value.toLowerCase())
    },
    [promotionalCodeField],
  )
  const handlePromotionalCodeFocus = useCallback(() => {
    setIsEditing(true)
    setIsFocused(true)
  }, [])
  const handlePromotionalCodeBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const nextFocusedElement = event.relatedTarget
      if (nextFocusedElement && rootRef.current?.contains(nextFocusedElement)) {
        promotionalCodeField.onBlur()
        return
      }
      setIsEditing(false)
      setIsFocused(false)
      promotionalCodeField.onBlur()
    },
    [promotionalCodeField],
  )
  useEffect(() => {
    if (promotionalCodeBootstrapping) {
      return
    }
    if (appliedPromotionalCodeId) {
      setIsEditing(false)
      setIsFocused(false)
    }
  }, [appliedPromotionalCodeId, promotionalCodeBootstrapping])
  const status = useMemo(() => {
    if (promoCodeSubmitting) {
      return 'loading'
    }
    if (promotionalCodeBootstrapping) {
      return 'loading'
    }
    if (!isEditing && hasAppliedPromo) {
      return 'valid'
    }
    if (promotionalCodeFieldError?.message) {
      return 'invalid'
    }
    return 'idle'
  }, [
    hasAppliedPromo,
    isEditing,
    promotionalCodeBootstrapping,
    promotionalCodeFieldError?.message,
    promoCodeSubmitting,
  ])
  const containerSx = useMemo(
    () => ({
      borderRadius: '5px',
      '&:hover': {
        boxShadow: (theme: Theme) =>
          status === 'invalid'
            ? !isFocused
              ? 'inset 0 0 0 1px #000000, 0 0 0 1px #df1b41'
              : 'inset 0 0 0 1px #000000, 0 0 0 1px transparent'
            : status === 'valid'
              ? '0 0 0 1px #0fc39a'
              : `inset 0 0 0 1px ${isFocused ? theme.palette.primary.main : '#000000'}, 0 0 0 1px transparent`,
      },
      backgroundColor: status === 'valid' ? '#ecfaf6' : '#ffffff',
      minHeight: 44,
      padding: '0 12px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      boxShadow:
        status === 'invalid'
          ? 'inset 0 0 0 1px #df1b41, 0 0 0 1px #df1b41'
          : status === 'valid'
            ? '0 0 0 1px #0fc39a'
            : `inset 0 0 0 1px #c5c7cb, 0 0 0 1px transparent`,
    }),
    [isFocused, status],
  )
  return (
    <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography
        sx={{
          fontSize: '0.93rem',
          marginBottom: 0.5,
          lineHeight: '1.15',
          color: '#30313d',
        }}
      >
        Promotional code
      </Typography>
      {promotionalCodeBootstrapping ? (
        <Box sx={containerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <CircularProgress delay={150} size={18} />
            <Typography variant="body1" sx={{ color: '#5f6777' }}>
              Validating promotional code...
            </Typography>
          </Box>
        </Box>
      ) : !isEditing && hasAppliedPromo && appliedPromotionalCode ? (
        <Box sx={containerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 22,
                height: 22,
                minWidth: 22,
                borderRadius: '50%',
                backgroundColor: '#0fc39a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: '16px',
                  fontWeight: 700,
                  lineHeight: 1.1,
                  textTransform: 'lowercase',
                }}
              >
                {appliedPromotionalCode.id}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: '#7d8696', lineHeight: 1.1, fontSize: '12px' }}
              >
                {getTitleByCodeMode({
                  promotionalCode: appliedPromotionalCode,
                })
                  .replace(/^\(/, '')
                  .replace(/\)$/, '')}
              </Typography>
            </Box>
            <IconButton
              onClick={handleRemoveAppliedPromoCode}
              disabled={disabled || promoCodeSubmitting}
              size="small"
              sx={{ color: '#8d95a3', padding: 0.25 }}
            >
              <CloseRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box sx={containerSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <InputBase
              {...promotionalCodeField}
              value={(promotionalCodeField.value || '').toLowerCase()}
              placeholder="e.g. summer95"
              disabled={disabled || promoCodeSubmitting}
              autoComplete="off"
              onFocus={handlePromotionalCodeFocus}
              onBlur={handlePromotionalCodeBlur}
              onChange={handlePromotionalCodeChange}
              sx={{
                flex: 1,
                fontSize: '16px',
                color: status === 'invalid' ? '#df1b41' : '#30313d',
                '& input::placeholder': {
                  color: '#757680',
                  opacity: 1,
                },
              }}
            />
            <Button
              variant="text"
              onClick={handleApplyPromoCode}
              disabled={disabled || promoCodeSubmitting}
              sx={{
                minWidth: 0,
                padding: 0,
                fontSize: '16px',
                fontWeight: 500,
                textTransform: 'none',
                minHeight: 27,
              }}
            >
              {promoCodeSubmitting ? <CircularProgress delay={150} size={20} /> : 'Apply'}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default PromoBox
